import {useEffect, useMemo, useState} from 'react';

interface Params {
    avatarUrl: string | null | undefined;
    hasUploadedAvatar: boolean;
}

interface Result {
    displayedAvatarUrl: string;
    shouldShowUploadedAvatar: boolean;
}

/**
 * Preloads avatar URLs and keeps showing the last loaded avatar during URL swaps.
 *
 * @param params Input values for preload behavior.
 * @param params.avatarUrl Raw avatar URL from entity data.
 * @param params.hasUploadedAvatar Whether an uploaded avatar is currently expected.
 * @returns Hook result with:
 * - `displayedAvatarUrl`: the currently safe-to-render loaded avatar URL.
 * - `shouldShowUploadedAvatar`: whether to render `Avatar` instead of fallback icon UI.
 */
export function usePreloadAvatar(params: Params): Result {
    const normalizedAvatarUrl = (params.avatarUrl ?? '').trim();
    const hasUploadedAvatarWithUrl = params.hasUploadedAvatar && Boolean(normalizedAvatarUrl);

    const [displayedAvatarUrl, setDisplayedAvatarUrl] = useState<string>(
        hasUploadedAvatarWithUrl ? normalizedAvatarUrl : ''
    );

    const avatarUrlWithParams = useMemo(() => {
        if (!normalizedAvatarUrl) {
            return '';
        }

        const imageSizeParams = 'width=100&height=100';
        return normalizedAvatarUrl.includes('?')
            ? `${normalizedAvatarUrl}&${imageSizeParams}`
            : `${normalizedAvatarUrl}?${imageSizeParams}`;
    }, [normalizedAvatarUrl]);

    useEffect(() => {
        if (!hasUploadedAvatarWithUrl) {
            setDisplayedAvatarUrl('');
            return;
        }

        if (typeof Image === 'undefined') {
            setDisplayedAvatarUrl(normalizedAvatarUrl);
            return;
        }

        let isCancelled = false;

        const image = new Image();
        image.onload = () => {
            if (!isCancelled) {
                setDisplayedAvatarUrl(normalizedAvatarUrl);
            }
        };
        image.src = avatarUrlWithParams;

        return () => {
            isCancelled = true;
        };
    }, [avatarUrlWithParams, hasUploadedAvatarWithUrl, normalizedAvatarUrl]);

    return {
        displayedAvatarUrl,
        shouldShowUploadedAvatar: params.hasUploadedAvatar && Boolean(displayedAvatarUrl),
    };
}
